import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { securityApi } from '../services/securityApi';
import type { CanonicalEventDetail } from '../types/security';

export function useCanonicalEventDetail(canonicalEventId: string | null) {
  const { authFetch } = useAuth();
  const [data, setData] = useState<CanonicalEventDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const fetchNow = useCallback(async () => {
    if (!canonicalEventId) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    const myId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const next = await securityApi.getCanonicalEvent(authFetch, canonicalEventId);
      if (myId !== requestIdRef.current) return;
      setData(next);
    } catch (e) {
      if (myId !== requestIdRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setData(null);
    } finally {
      if (myId === requestIdRef.current) setIsLoading(false);
    }
  }, [authFetch, canonicalEventId]);

  useEffect(() => {
    fetchNow();
  }, [fetchNow]);

  return { data, isLoading, error, refetch: fetchNow };
}
